import { CONFIGURATION } from '../setup/component-lib.esm.js';
import { defaultWindow } from './component-lib.esm5.js';
import { removeEmptyFromObject } from './component-lib.esm20.js';
const MEDIA_SIZES = {
  thumbnail: 200,
  low_resolution: 320,
  standard_resolution: 640,
  high_resolution: 1080,
  profile_image: 160
};
const CROP_TYPES = {
  cover: 'cover',
  contain: 'contain',
  fill: 'fill',
  inside: 'inside',
  outside: 'outside'
};

/**
 * Checks whether the current URL is pointing to an icon
 * @param {String} url
 * @returns {Boolean} Whether the URL is an icon
 */
function isImageIcon(url) {
  // Starts with <svg
  return url === null || url === void 0 ? void 0 : url.startsWith('<svg');
}

/**
 * Takes an image URL and options and encodes it
 * @param {String} url - The URL of the image
 * @param {Object} options Options to pass to sharp
 * @returns The encoded URL of the image
 */
function getEncodedAssetUrl(url) {
  let options = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
  const {
    width,
    height,
    fit,
    background,
    ...rest
  } = options;
  const newUrl = new URL(url);
  const resize = removeEmptyFromObject({
    width,
    height,
    fit,
    background
  });
  const edits = removeEmptyFromObject({
    resize: Object.keys(resize).length ? resize : undefined,
    ...rest
  });
  const payload = JSON.stringify({
    bucket: CONFIGURATION['assetsBucketName'],
    key: newUrl.pathname.slice(1),
    edits: Object.keys(edits).length ? edits : undefined
  });

  // console.log(payload);
  let encodedPath;
  if (defaultWindow !== null && defaultWindow !== void 0 && defaultWindow.btoa) encodedPath = btoa(payload);else encodedPath = Buffer.from(payload).toString('base64');
  return `${CONFIGURATION['assetCdn']}/${encodedPath}`;
}

/**
 * Takes a media URL and crops it to a square
 * @param {String} url The original URL of the media
 * @param {Number} size The size of the square
 * @param {Object} options Options to pass to sharp
 * @returns The encoded URL of the media image
 */
function getCroppedMediaUrl(url) {
  let width = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : MEDIA_SIZES.low_resolution;
  let options = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : {};
  if (typeof url === 'string' && url.includes(CONFIGURATION['assetCdn'])) {
    return getEncodedAssetUrl(url, {
      width,
      ...options
    });
  }
  return url;
}

/**
 * Takes a media URL and returns the URLs for a background image
 * @param {String} url
 * @returns An object with three URLs
 */
function getBackgroundImageUrlsFromMediaUrl(url) {
  return {
    mobile: getEncodedAssetUrl(url, {
      width: 544,
      height: 900,
      fit: CROP_TYPES.cover
    }),
    tablet: getEncodedAssetUrl(url, {
      width: 992,
      height: 1200,
      fit: CROP_TYPES.cover
    }),
    desktop: getEncodedAssetUrl(url, {
      width: 1920,
      height: 1200,
      fit: CROP_TYPES.cover
    })
  };
}

/**
 * Takes a media URL and returns the URLs for a cover image
 * @param {String} url
 * @returns An object with four URLs
 */
function getCoverImageUrlsFromMediaUrl(url) {
  return {
    mobile: getEncodedAssetUrl(url, {
      width: 400,
      height: 715,
      fit: CROP_TYPES.cover
    }),
    mobile2x: getEncodedAssetUrl(url, {
      width: 800,
      height: 1430,
      fit: CROP_TYPES.cover
    }),
    desktop: getEncodedAssetUrl(url, {
      width: 475,
      height: 850,
      fit: CROP_TYPES.cover
    }),
    desktop2x: getEncodedAssetUrl(url, {
      width: 950,
      height: 1700,
      fit: CROP_TYPES.cover
    })
  };
}

/**
 * Takes a media URL and returns the URLs for a cover image
 * @param {String} url Raw unsplash URL
 * @returns An object with four URLs
 */
function getCoverImageUrlsFromUnsplashUrl(url) {
  return {
    mobile: `${url}&w=400&h=715&fit=crop`,
    mobile2x: `${url}&w=800&h=1430&fit=crop`,
    desktop: `${url}&w=475&h=850&fit=crop`,
    desktop2x: `${url}&w=950&h=1700&fit=crop`
  };
}

// Get a mimetype from a file extension
function getMimeType(ext) {
  switch (ext) {
    case 'jpg':
    case 'jpeg':
      return 'image/jpeg';
    case 'png':
      return 'image/png';
    case 'gif':
      return 'image/gif';
    case 'webp':
      return 'image/webp';
    default:
      return 'image/jpeg';
  }
}

/**
 * Given a URL, returns a base64 encoded URL if it's an image
 * @param {String} url
 * @param {Integer} width
 * @param {Object} options
 * @returns A base64 encoded image URL
 */
function convertImageUrlToEncodedUrl(url) {
  let width = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : MEDIA_SIZES.low_resolution;
  let options = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : {};
  // If the URL has our asset CDN, get the encoded URL
  if (typeof url === 'string' && url.includes(CONFIGURATION['assetCdn'])) {
    return getCroppedMediaUrl(url, width, options);
  }
  return url;
}
export { CROP_TYPES, MEDIA_SIZES, convertImageUrlToEncodedUrl, getBackgroundImageUrlsFromMediaUrl, getCoverImageUrlsFromMediaUrl, getCoverImageUrlsFromUnsplashUrl, getCroppedMediaUrl, getEncodedAssetUrl, getMimeType, isImageIcon };